// ---------------------------------------------------------------------------
// Consent manager — the single source of truth for whether marketing
// tracking (the Meta Pixel) is allowed for this visitor.
//
// Design goals:
//   - Essential cookies (session, anonymous-session, UI prefs) are never
//     gated. This module governs ONLY the marketing category.
//   - Privacy-safe by construction: the pixel loads only when this module
//     says marketing is granted. Nothing from Meta touches the page before
//     that.
//   - No external calls. Region is inferred from the browser's own timezone
//     (Intl), so there is no IP-geo round-trip and no third-party dependency.
//
// Decision logic on first visit (no stored choice yet):
//   1. Global Privacy Control set        -> DENIED, no banner (honor the
//                                            browser-level signal silently).
//   2. Consent-required region (EU/UK/    -> UNKNOWN, show the banner, pixel
//      EEA/CH, by timezone)                  stays OFF until the visitor accepts.
//   3. Everywhere else (mostly US)       -> GRANTED (opt-out regime), pixel
//                                            loads; a brief, dismissible note
//                                            explains how to opt out.
//   4. Timezone unreadable               -> treated as consent-required
//                                            (the safe direction).
//
// A stored choice always wins over all of the above.
// ---------------------------------------------------------------------------

export type ConsentStatus = 'granted' | 'denied' | 'unknown';

/**
 * Master switch for ALL third-party tracking in the browser (Google Ads tag,
 * Meta, LinkedIn and Reddit pixels). Turn it off to silence every tracker at
 * once with no banner; the server has the matching switch in
 * methods/src/common/tracking.ts.
 *
 * ON since 2026-09-26, CONSENT-FIRST FOR EVERY VISITOR: nothing third-party
 * loads until the visitor taps Accept, wherever they are. There is no longer
 * an opt-out region.
 */
export const THIRD_PARTY_TRACKING_ENABLED = true;

interface StoredConsent {
  /** Advertising tools (Google Ads, Meta, LinkedIn, Reddit). Essential is always on. */
  marketing: 'granted' | 'denied';
  /**
   * Analytics (Microsoft Clarity session insights). Added 2026-09-29; choices
   * stored before then have no value here and inherit the marketing choice.
   */
  analytics?: 'granted' | 'denied';
  /** Schema version, so a future category change can re-prompt cleanly. */
  v: number;
  /** When the choice was made (unix ms), for our own audit/debugging. */
  at: number;
}

const STORAGE_KEY = 'entredash.consent.v1';
// v2 (2026-09-26): consent-first for everyone. Choices stored under v1 (region
// model, where some visitors were opted in by default) are ignored, so every
// visitor is asked fresh.
const SCHEMA_VERSION = 2;

/**
 * Timezone prefixes that indicate a consent-required jurisdiction (EU/EEA,
 * UK, Switzerland). We deliberately treat ALL `Europe/*` zones as
 * consent-required: this over-includes a few non-EEA countries (e.g. Turkey,
 * Russia, Ukraine), which is the safe direction — showing a consent banner to
 * someone who didn't strictly need it costs nothing, while failing to show it
 * to someone who did is the actual risk. A handful of EEA territories sit on
 * Atlantic zones (Iceland, Azores/Madeira, Canary Islands) and are listed
 * explicitly.
 */
const CONSENT_REQUIRED_TZ_PREFIXES = ['Europe/'];
const CONSENT_REQUIRED_TZ_EXACT = new Set([
  'Atlantic/Reykjavik', // Iceland (EEA)
  'Atlantic/Azores', // Portugal (EEA)
  'Atlantic/Madeira', // Portugal (EEA)
  'Atlantic/Canary', // Spain (EEA)
]);

/**
 * United States and Canada IANA timezones. Visitors here stay on the opt-out
 * footing (the pixel is allowed) but we suppress the proactive opt-out note —
 * the footer "Cookie preferences" link remains the standing opt-out path. These
 * zones are country-specific in the tz database, so a match reliably indicates
 * a US or Canadian visitor (someone in Mexico reports America/Mexico_City, in
 * Colombia America/Bogota, etc.). Kept as an explicit set because the America/
 * prefix spans all of the Americas.
 */
const US_CANADA_TZ_EXACT = new Set([
  // ---- United States (contiguous, Alaska, Hawaii, Indiana/Kentucky/Dakotas) ----
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Chicago',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
  'America/Puerto_Rico', // Puerto Rico (US)
  'America/St_Thomas', // US Virgin Islands
  'Pacific/Guam',
  'Pacific/Saipan', // Northern Mariana Islands (US)
  'Pacific/Pago_Pago', // American Samoa (US)
  // ---- Canada ----
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Blanc-Sablon',
  'America/Toronto',
  'America/Montreal', // legacy alias, some browsers still report it
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Iqaluit',
  'America/Pangnirtung',
  'America/Atikokan',
  'America/Winnipeg',
  'America/Rainy_River',
  'America/Resolute',
  'America/Rankin_Inlet',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Yellowknife',
  'America/Inuvik',
  'America/Creston',
  'America/Dawson_Creek',
  'America/Fort_Nelson',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Dawson',
]);

/**
 * Timezones that carry NO location information — privacy browsers (Brave,
 * Firefox with resistFingerprinting) report `UTC`/`Etc/UTC` regardless of where
 * the user actually is, and some hardened setups report nothing. A genuine
 * UTC-resident is vanishingly rare (Iceland reports Atlantic/Reykjavik, the UK
 * reports Europe/London — both handled elsewhere), so an ambiguous zone is
 * overwhelmingly a US/Canada visitor whose browser hid their timezone. We treat
 * it as "suppress the note" (fail closed) rather than defaulting it visible.
 */
const AMBIGUOUS_TZ = new Set(['UTC', 'Etc/UTC', 'Etc/GMT', 'GMT', 'Etc/Unknown']);

/** 2-letter region from a BCP-47 locale tag, e.g. "en-US" -> "US". */
function regionFromLocale(tag: string | undefined | null): string | null {
  if (!tag) return null;
  // Region subtag is the 2-letter (or 3-digit) part after the language, e.g.
  // en-US, fr-CA, es-419. Grab the first 2-letter uppercase-able segment.
  const parts = tag.split('-');
  for (const p of parts.slice(1)) {
    if (/^[A-Za-z]{2}$/.test(p)) return p.toUpperCase();
  }
  return null;
}

/**
 * Whether to SUPPRESS the proactive opt-out note for this visitor — the
 * US/Canada case the note was turned off for. This does NOT change whether the
 * pixel is allowed (US/Canada stay on the opt-out footing); it only hides the
 * proactive note. Opt-out always remains reachable via the footer.
 *
 * The platform exposes no server-side IP/geo signal, so location is inferred
 * from the browser. Timezone alone missed real US/Canada visitors (privacy
 * browsers report UTC; the note then popped up for them — the bug this fixes).
 * We now suppress when ANY reliable-enough signal points to US/Canada, and
 * FAIL CLOSED on ambiguity, because over-showing the note is friction while
 * under-showing it is harmless (the footer control still exists):
 *
 *   1. Browser timezone is a US/Canada zone, OR
 *   2. The browser's resolved locale/language region is US or CA
 *      (catches privacy browsers that spoof timezone but keep en-US/en-CA), OR
 *   3. The timezone carries no location (UTC/unknown) — treated as US/Canada.
 *
 * EU/UK visitors never reach this: they are caught earlier by
 * `isConsentRequiredRegion` and shown the blocking banner instead.
 */
export function isOptOutNoteSuppressedRegion(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 1. Explicit US/Canada timezone.
    if (tz && US_CANADA_TZ_EXACT.has(tz)) return true;

    // 3. No usable timezone (privacy browser / hardened setup) -> fail closed.
    if (!tz || AMBIGUOUS_TZ.has(tz)) return true;

    // 2. Locale/language region is US or CA. Check the Intl-resolved locale
    // first, then navigator languages, since a spoofed timezone usually leaves
    // these intact.
    const candidates: Array<string | undefined> = [];
    try {
      candidates.push(Intl.DateTimeFormat().resolvedOptions().locale);
    } catch {
      /* ignore */
    }
    if (typeof navigator !== 'undefined') {
      candidates.push(navigator.language);
      if (Array.isArray(navigator.languages)) candidates.push(...navigator.languages);
    }
    for (const c of candidates) {
      const region = regionFromLocale(c);
      if (region === 'US' || region === 'CA') return true;
    }

    return false;
  } catch {
    // On total failure, fail closed (suppress) — the footer control remains, so
    // a missing note is harmless, whereas a spurious note is the reported issue.
    return true;
  }
}

/** Read the browser's Global Privacy Control signal, if present. */
function hasGlobalPrivacyControl(): boolean {
  try {
    return (
      typeof navigator !== 'undefined' &&
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl === true
    );
  } catch {
    return false;
  }
}

/**
 * Whether this visitor is in a consent-required region, inferred from the
 * browser timezone. On any failure we return `true` (the safe default:
 * require consent).
 */
export function isConsentRequiredRegion(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return true; // unreadable -> safe default
    if (CONSENT_REQUIRED_TZ_EXACT.has(tz)) return true;
    return CONSENT_REQUIRED_TZ_PREFIXES.some((p) => tz.startsWith(p));
  } catch {
    return true; // safe default
  }
}

function readStored(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (
      parsed &&
      parsed.v === SCHEMA_VERSION &&
      (parsed.marketing === 'granted' || parsed.marketing === 'denied')
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStored(marketing: 'granted' | 'denied', analytics: 'granted' | 'denied' = marketing): void {
  try {
    const payload: StoredConsent = { marketing, analytics, v: SCHEMA_VERSION, at: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage unavailable (private mode / blocked) — the in-memory
    // state below still governs this session; we just can't persist it.
  }
}

// ---- In-memory state + subscription ----

type Listener = () => void;

interface ConsentState {
  /** Marketing consent status for the pixel. */
  status: ConsentStatus;
  /** Analytics consent status (Clarity). */
  analytics: ConsentStatus;
  /** True when the visitor is in a consent-required region. */
  regionRequired: boolean;
  /** True when the browser signaled Global Privacy Control. */
  gpc: boolean;
  /** Whether the visitor has made an explicit stored choice. */
  decided: boolean;
  /**
   * True for US/Canada visitors: the pixel still runs on the opt-out footing,
   * but the proactive opt-out note is suppressed (opt-out stays available via
   * the footer "Cookie preferences" link).
   */
  suppressNote: boolean;
}

let state: ConsentState = computeInitialState();
const listeners = new Set<Listener>();

function computeInitialState(): ConsentState {
  // Third-party tracking switched off: denied for everyone, no banner.
  if (!THIRD_PARTY_TRACKING_ENABLED) {
    return { status: 'denied', analytics: 'denied', regionRequired: false, gpc: false, decided: true, suppressNote: true };
  }
  // SSR / non-browser guard (defensive; this app is a SPA).
  if (typeof window === 'undefined') {
    return { status: 'denied', analytics: 'denied', regionRequired: true, gpc: false, decided: false, suppressNote: false };
  }

  const gpc = hasGlobalPrivacyControl();
  const regionRequired = isConsentRequiredRegion();
  const suppressNote = isOptOutNoteSuppressedRegion();
  const stored = readStored();

  if (stored && stored.analytics !== undefined) {
    // An explicit stored choice always wins.
    return { status: stored.marketing, analytics: stored.analytics, regionRequired, gpc, decided: true, suppressNote };
  }
  if (stored) {
    // A choice made before analytics had its own toggle: the marketing answer stands, but analytics (session
    // recording) was never asked about, so it stays off until the visitor turns it on in Cookie preferences.
    return { status: stored.marketing, analytics: 'denied', regionRequired, gpc, decided: true, suppressNote };
  }

  if (gpc) {
    // Honor the browser-level opt-out silently. No banner.
    return { status: 'denied', analytics: 'denied', regionRequired, gpc, decided: false, suppressNote };
  }

  // Consent-first for EVERY visitor: nothing loads until they accept. The
  // region is still computed (kept on state for reference) but no longer
  // grants anything by default.
  return { status: 'unknown', analytics: 'unknown', regionRequired: true, gpc, decided: false, suppressNote };

  // Opt-out regime (mostly US): pixel is allowed; a dismissible note explains
  // how to opt out. Not stored as "decided" so the note can show once. For
  // US/Canada visitors `suppressNote` hides that note (opt-out stays reachable
  // from the footer) while leaving the pixel on the same opt-out footing.
  return { status: 'granted', analytics: 'granted', regionRequired, gpc, decided: false, suppressNote };
}

function emit(): void {
  for (const l of listeners) l();
}

export function getConsentState(): ConsentState {
  return state;
}

/**
 * The consent decision in the shape the SERVER persists, or null when the
 * visitor has not resolved one.
 *
 * The server needs this because advertising events also fire from paths with no
 * browser involvement at all — most importantly the Stripe webhook. Without a
 * stored copy those paths had nothing to consult and transmitted regardless of
 * what the visitor chose.
 *
 * Returns null rather than a default when undecided. An unresolved state must
 * reach the server as unknown, which every sender treats as denied; inventing
 * a value here would manufacture consent that was never given.
 */
export function resolvedConsentForServer():
  | { granted: boolean; source: 'banner' | 'opt_out_region' | 'gpc' | 'preferences' }
  | null {
  // Nothing to consent to while third-party tracking is off; unknown = denied.
  if (!THIRD_PARTY_TRACKING_ENABLED) return null;
  // Global Privacy Control is a legally recognised opt-out signal in several
  // jurisdictions, so it is recorded as an explicit denial rather than as an
  // absence of choice.
  if (state.gpc) return { granted: false, source: 'gpc' };
  if (state.decided) {
    if (state.status === 'granted') return { granted: true, source: 'preferences' };
    if (state.status === 'denied') return { granted: false, source: 'banner' };
  }
  // Opt-out region with the pixel already live and the note not yet dismissed:
  // a real, if implied, grant under that region's rules.
  if (!state.regionRequired && state.status === 'granted') {
    return { granted: true, source: 'opt_out_region' };
  }
  return null;
}

/**
 * Routes whose URL is itself a secret: a shared memo link (/shared/<doc>/<token>)
 * and a drawings resume link. The token in the address unlocks confidential
 * content, so no ad tag, pixel or session recorder may ever see these URLs,
 * whatever the visitor consented to.
 */
const SECRET_URL_PREFIXES = ['/shared/', '/witness/', '/merge-accounts/', '/invention-concept-drawings/resume/'];

export function isSecretUrlRoute(path?: string): boolean {
  const p = path ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return SECRET_URL_PREFIXES.some((prefix) => p.startsWith(prefix));
}

/** Is marketing tracking (the pixel) currently allowed? */
export function isMarketingGranted(): boolean {
  if (!THIRD_PARTY_TRACKING_ENABLED) return false;
  // Never on a secret-URL route, even with consent (see isSecretUrlRoute).
  if (isSecretUrlRoute()) return false;
  return state.status === 'granted';
}

/**
 * Should the blocking consent banner be shown right now? Only in a
 * consent-required region, only when the visitor hasn't decided, and never
 * when GPC already settled it.
 */
export function shouldShowBanner(): boolean {
  if (!THIRD_PARTY_TRACKING_ENABLED) return false;
  return state.regionRequired && !state.decided && !state.gpc && state.status === 'unknown';
}

/**
 * Should the brief, non-blocking privacy note be shown? Opt-out regions
 * only, where the pixel is already live but the visitor hasn't acknowledged
 * or opted out yet.
 */
/**
 * Master switch for the proactive opt-out note.
 *
 * RETIRED (product decision): set to `false` so the note is never shown to
 * anyone. It was creating friction on the hero — most notably for US/Canada
 * visitors, whom it was never meant to interrupt. Flip back to `true` to
 * restore the original region-aware behavior below; nothing else needs to
 * change.
 *
 * What is UNAFFECTED:
 *  - The EU/UK/EEA/CH blocking banner (driven by `regionRequired`) still shows
 *    and remains the legal-compliance path.
 *  - Global Privacy Control is still honored silently.
 *  - Opt-out stays fully available any time via the footer "Cookie preferences"
 *    link, and the pixel still runs on the opt-out footing for non-consent
 *    regions — so the CCPA/PIPEDA opt-out path and what's collected are unchanged.
 */
const OPT_OUT_NOTE_ENABLED = false;

export function shouldShowOptOutNote(): boolean {
  if (!OPT_OUT_NOTE_ENABLED) return false;
  return (
    !state.regionRequired &&
    !state.decided &&
    !state.gpc &&
    !state.suppressNote &&
    state.status === 'granted'
  );
}

/** Is analytics (Clarity) currently allowed? */
export function isAnalyticsGranted(): boolean {
  if (!THIRD_PARTY_TRACKING_ENABLED) return false;
  if (isSecretUrlRoute()) return false;
  return state.analytics === 'granted';
}

/** Record an explicit choice for both categories (Accept all / Reject all). */
export function setMarketingConsent(granted: boolean): void {
  setConsentChoices({ marketing: granted, analytics: granted });
}

/** Record an explicit per-category choice (from the preferences panel). */
export function setConsentChoices(choice: { marketing: boolean; analytics: boolean }): void {
  if (!THIRD_PARTY_TRACKING_ENABLED) return;
  const m = choice.marketing ? 'granted' : 'denied';
  const a = choice.analytics ? 'granted' : 'denied';
  writeStored(m, a);
  state = { ...state, status: m, analytics: a, decided: true };
  emit();
}

/**
 * Dismiss the opt-out note without changing the (granted) status. Marks the
 * visitor as decided so the note doesn't reappear, while leaving the pixel on.
 */
export function acknowledgeOptOut(): void {
  writeStored('granted');
  state = { ...state, status: 'granted', decided: true };
  emit();
}

/**
 * Re-open a choice from the footer "Cookie preferences" link. Clears the
 * decided flag so the preferences UI treats it as an open question again,
 * without wiping the current status (shown as the current selection).
 */
export function reopenPreferences(): void {
  if (!THIRD_PARTY_TRACKING_ENABLED) return;
  state = { ...state, decided: false };
  emit();
  // Broadcast so the preferences panel can open regardless of where the
  // click came from.
  try {
    window.dispatchEvent(new CustomEvent('entredash:open-consent-preferences'));
  } catch {
    /* no-op */
  }
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
